import { useEffect, useMemo, useState } from 'react';
import type { IResult } from 'ua-parser-js';
import { logError } from '../../../utils/error-reporting/log';
import type { WebUserExtraContext } from '../../stores/GlobalStateStore';
import type { UseContextReturn } from './types';

// These are the dimesions of the example iphone shown in UI builder.
const DEFAULT_DEVICE_DIMENSIONS = {
  width: 388,
  height: 748,
};

// eslint-disable-next-line @typescript-eslint/unbound-method -- safe to ignore
const isDOMAvailable = typeof window !== 'undefined' && Boolean(window.document.createElement);

interface UaPayload {
  uaPayload?: {
    deviceIdentifierId?: string;
    modelId?: string;
    modelName?: string;
  };
}

function getDeviceIdentifiers(): {
  deviceId?: string;
  modelId?: string;
  modelName?: string;
} {
  if (typeof window === 'undefined') return {};
  const payload = (window as unknown as UaPayload)?.uaPayload;
  return {
    deviceId: payload?.deviceIdentifierId,
    modelId: payload?.modelId,
    modelName: payload?.modelName,
  };
}

export function useUserRuntimeEnvDetails(): UseContextReturn {
  const [result, setResult] = useState<IResult | null>(null);
  const [isLoadingUserAgent, setIsLoadingUserAgent] = useState(true);

  useEffect(() => {
    if (!isDOMAvailable) {
      // no DOM (SSR / native) — the parser can never run, so resolve loading immediately.
      setIsLoadingUserAgent(false);
      return;
    }

    // lazy-loading the UAParser library
    // Ref: https://github.com/expo/expo/blob/main/packages/expo-device/src/ExpoDevice.web.ts
    import('ua-parser-js')
      .then(({ UAParser }) => {
        setResult(UAParser(window.navigator.userAgent));
      })
      .catch((error: unknown) => {
        // surface load failures (network/bundler) instead of silently falling back to "Unknown".
        logError(error);
      })
      // keep loading true until the async import settles so consumers don't treat the
      // fallback "Unknown" values as final mid-flight.
      .finally(() => {
        setIsLoadingUserAgent(false);
      });
  }, []);

  const contextValue = useMemo(() => {
    const { deviceId, modelId, modelName } = getDeviceIdentifiers();
    return {
      context: {
        device: {
          // device id is set while opening login page in react native webview using javascript injection,
          // this is required during login via biometric
          // ping @Pradeet for any queries
          deviceId,
          brand: result ? result.device.model : 'Unknown',
          os: {
            name: result ? result.os.name : '',
            version: result ? result.os.version : '',
          },
          dimensions: DEFAULT_DEVICE_DIMENSIONS,
          modelId,
          modelName,
        },
        browser: {
          name: result ? result.browser.name : 'Unknown',
          version: result ? result.browser.version : 'Unknown',
        },
      } as WebUserExtraContext,
      isLoadingContext: isLoadingUserAgent,
    };
  }, [result, isLoadingUserAgent]);

  return contextValue;
}
